import styled from "@emotion/styled"
import { keyframes } from "@emotion/react"
import React from "react"

import colors from "../../styles/colors"
import Link from "../link"

interface Hotspot {
  top?: number
  bottom?: number
  left: number
}
interface Highlight {
  top: number
  left: number
  bottom?: number // Highlights only grow vertically, by default they are 400px wide (screen-relative)
}

interface BaseCard {
  behaviour: string
  title: React.ReactNode
  description: React.ReactNode
}
interface WelcomeCard extends BaseCard {
  behaviour: "welcome"
}
interface HotspotCard extends BaseCard {
  behaviour: "hotspot"
  hotspot: Hotspot
  /** Forces the layouting engine to not swap the card (useful for cards that are close to the middle) */
  noSwap?: boolean
}
interface TourCard extends BaseCard {
  behaviour: "tour"
  /** Forces the layouting engine to not swap the card (useful for cards that are close to the middle) */
  noSwap?: boolean
  highlight?: Highlight
  hotspot: Hotspot
}
interface CTACard extends BaseCard {
  behaviour: "cta"
}

/** This card is not visible and indicates a timeout in the animation */
interface TimeoutCard {
  behaviour: "timeout"
  timeoutMs: number
}
type Card = WelcomeCard | HotspotCard | TourCard | CTACard | TimeoutCard

interface Slide {
  imgUrl: string
  cards: Card[]
}

const slides: Slide[] = [
  {
    imgUrl: require("../../images/testdrive/1274.png").default,
    cards: [
      {
        title: "Overview",
        description:
          "Before we get into the tour, let's do a quick overview to get you familiar with the layout of the agent workspace.",
        behaviour: "welcome",
      },
      {
        title: "Active conversations",
        description:
          "Ongoing conversations appear here. Agents can jump between conversations, and help multiple customers at a time.",
        behaviour: "tour",
        hotspot: { top: 120, left: 356 },
        highlight: { top: 16, left: -23 },
      },
      {
        title: "Conversation queue",
        description:
          "This is where agents can pick up incoming customer conversations.",
        behaviour: "tour",
        hotspot: { top: 430, left: 356 },
        highlight: { top: 292, left: -23 },
      },
      {
        title: "Ongoing conversation",
        description:
          "A seamless history of all interactions and messages between the customer, virtual assistant and support agents",
        behaviour: "tour",
        hotspot: { top: 410, left: 730 },
        noSwap: true,
        highlight: { top: 8, left: 351, bottom: 8 },
      },
      {
        title: "Video pane",
        description:
          "Video & voice calls with customers occur here. Agents can see exactly what the customer sees, and help to diagnose and resolve problems remotely. They can even draw on the screen to help direct customers with visual queues.",
        behaviour: "tour",
        hotspot: { top: 310, left: 730 },
        highlight: { top: 8, left: 710, bottom: 8 },
      },
      {
        title: "Information pane",
        description:
          "Agents get all available context on the customer here: name and contact details, product info, agents’ notes, and previous support tickets.",
        behaviour: "tour",
        hotspot: { top: 310, left: 1060 },
        highlight: { top: 8, left: 1054, bottom: 8 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1268.png").default,
    cards: [
      {
        title: "Meet John",
        description:
          "He is a senior support agent who helps customers troubleshoot and fix complex issues with their devices.",
        behaviour: "welcome",
      },
      {
        title: null,
        description:
          "John selects one of the new requests from the Conversations list.",
        behaviour: "hotspot",
        hotspot: { top: 224, left: 378 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1300.png").default,
    cards: [
      {
        title: "Smart queue and routing",
        description:
          "Customers are redirected to the right agent group based on type of issue, language, as well as agents’ workload. This is all configurable!",
        behaviour: "hotspot",
        hotspot: { top: 476, left: 445 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1266.png").default,
    cards: [
      {
        title: "Self-service transcripts",
        description:
          "John reads through what this customer has previously tried through self-service assistance to get up to speed. This customer has problems pairing their headphones with their gaming console.",
        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
      {
        title: null,
        description:
          "He also checks the customer’s details and support history to be fully prepared to assist.",
        behaviour: "hotspot",
        hotspot: { top: 230, left: 1085 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1269.png").default,
    cards: [
      {
        title: "Saved replies",
        description:
          "John quickly greets the customer with a saved welcome message.",
        behaviour: "hotspot",
        hotspot: { bottom: 80, left: 710 },
        noSwap: true,
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1270.png").default,
    cards: [
      {
        behaviour: "timeout",
        timeoutMs: 2000,
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1267.png").default,
    cards: [
      {
        behaviour: "tour",
        title: "",
        description:
          "He confirms to the customer that he has checked the previous troubleshooting steps.",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1271.png").default,
    cards: [
      {
        title: "Remote video support",
        description:
          "After a quick chat, the issue seems to be somewhat complex and the customer is struggling with the troubleshooting instructions. John suggests to start a video call.",
        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1272.png").default,
    cards: [
      {
        title: "Powerful visual guidance",
        description:
          "John identifies where the problem is and draws on the video to guide the customer. He can also take screenshots and quickly add them to the notes panel for future reference.",
        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1296.png").default,
    cards: [
      {
        title: "Conversation debrief",
        description: (
          <>
            Problem solved! John was quite creative and suggested a new way to
            solve the issue. He can now capture it to help improve the AI
            self-service.
            <br />
            <br />
            Next time someone has the same issue, it’s likely to be resolved by
            the automated assistant.
          </>
        ),
        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1275.png").default,
    cards: [
      {
        title: "Conversation history",
        description:
          "There are no new requests in the queue, so John decides to follow up on chats that the team missed during peak hour, and to do quality checks on junior team members he is mentoring.",

        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1276.png").default,
    cards: [
      {
        title: "",
        description: (
          <>
            He goes to the Previous conversations feed where he can browse and
            search through his team’s history.
            <br />
            <br />
            He selects an unresolved conversation.
          </>
        ),
        behaviour: "tour",
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1297.png").default,
    cards: [
      {
        behaviour: "tour",
        title: "Invite customers to live support",
        description: (
          <>
            John sends an SMS to a customer whose request was missed earlier,
            and invites him to chat. He assigns this conversation to himself.
            <br />
            <br />
            The customer will be fast-tracked to live support and routed to
            John.
          </>
        ),
        hotspot: { bottom: 80, left: 1465 },
      },
    ],
  },
  {
    imgUrl: require("../../images/testdrive/1276.png").default,
    cards: [
      {
        behaviour: "cta",
        title: "Get a custom demo",
        description:
          "Now that you’ve seen some of the capabilities of the Agent dashboard, how about a custom demo? We’ll show you in detail how Mavenoid is the perfect tool for your organisation’s support needs.",
      },
    ],
  },
]

interface TourState {
  currentSlide: number
  currentCard: number
}
type TourAction = { type: "next" } | { type: "prev" }

const nextReducer = (state: TourState): TourState => {
  const currentSlide = slides[state.currentSlide]
  if (currentSlide.cards.length - 1 > state.currentCard)
    return {
      ...state,
      currentCard: state.currentCard + 1,
    }

  if (slides.length - 1 > state.currentSlide)
    return {
      ...state,
      currentSlide: state.currentSlide + 1,
      currentCard: 0,
    }
  return state
}
const prevReducer = (state: TourState): TourState => {
  if (state.currentCard > 0)
    return {
      ...state,
      currentCard: state.currentCard - 1,
    }

  if (state.currentSlide > 0)
    return {
      ...state,
      currentSlide: state.currentSlide - 1,
      currentCard: slides[state.currentSlide - 1].cards.length - 1,
    }

  return state
}

// Hitting "previous" just after a timeout card should roll back to whatever interactive card was there before
const skipTimeoutOnPrev = (state: TourState): TourState => {
  const currentCard = slides[state.currentSlide].cards[state.currentCard]
  if (currentCard.behaviour === "timeout")
    return skipTimeoutOnPrev(prevReducer(state))
  return state
}

const tourReducer = (state: TourState, action: TourAction): TourState => {
  switch (action.type) {
    case "next":
      return nextReducer(state)
    case "prev":
      return skipTimeoutOnPrev(prevReducer(state))
    default:
      return state
  }
}
const initialState: TourState = {
  currentSlide: 0,
  currentCard: 0,
}

const MainImg = styled.img`
  width: 100%;
`
const FullscreenBackdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
`
const CardContainer = styled.div`
  position: absolute;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  background: ${colors.white};
  color: ${colors.darkgrey};
  border-radius: 6px;
  box-shadow: 0px 4px 30px rgba(0, 0, 0, 0.15);
`
const CenterCardContainer = styled(CardContainer)`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
`
const CardDescription = styled.p`
  font-size: 14px;
  margin-top: 0;
  font-weight: 200;
  margin-bottom: 16px;
`
const CardFooter = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
`
const CardButton = styled.button`
  all: unset;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 200;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`
const CardButtonCTA = styled.button`
  all: unset;
  margin: 16px 0;
  background-color: ${colors.primary};
  color: ${colors.white};
  font-size: 14px;
  font-weight: 200;
  cursor: pointer;
  padding: 7px;
  text-align: center;
  border-radius: 100px;
`

const HotspotButton = styled.button`
  all: unset;
  position: absolute;
  width: 30px;
  height: 30px;
  transform: translate(-50%, -50%);
  cursor: pointer;
`
const HotspotMainCircle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 100px;
  background-color: ${colors.primary};
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const hotspotPulse = keyframes`
  from {
    transform: scale(0.5);
    opacity: 1;
  } to {
    transform: scale(2);
    opacity: 0;
  }
`
const HotspotPulseCircle = styled(HotspotMainCircle)`
  animation: ${hotspotPulse} 2s infinite ease-out;
  pointer-events: none;
`

const FRAME_HEIGHT = 800
const FRAME_WIDTH = 1440

const makeAdapter = (baseLength: number) => (length: number) =>
  length && `${(100 * length) / baseLength}%`

const adaptWidth = makeAdapter(FRAME_WIDTH)
const adaptHeight = makeAdapter(FRAME_HEIGHT)

interface HotspotProps extends Hotspot {
  onClick: () => void
}
const Hotspot = ({ top, bottom, left, onClick }: HotspotProps) => (
  <HotspotButton
    onClick={onClick}
    style={{
      top: adaptHeight(top),
      bottom: adaptHeight(bottom),
      left: adaptWidth(left),
    }}
  >
    <HotspotMainCircle />
    <HotspotPulseCircle />
  </HotspotButton>
)

const HighlightElement = styled.div`
  position: absolute;
  pointer-events: none;
  transition: opacity 0.5s, top 0.2s ease-out, bottom 0.2s ease-out,
    left 0.2s ease-out;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.6);
  border-radius: 1000px;
  min-width: ${adaptWidth(400)};
  min-height: ${adaptHeight(400)};
`

const CardContainerAtPos: React.FC<Hotspot & {
  noSwap: boolean
} & React.HTMLAttributes<HTMLDivElement>> = ({
  top,
  bottom,
  left,
  noSwap,
  children,
  ...props
}) => (
  <CardContainer
    {...props}
    style={{
      top: adaptHeight(top),
      bottom: adaptHeight(bottom),
      left:
        noSwap || left < FRAME_WIDTH / 2
          ? `calc(${adaptWidth(left)} + 40px)`
          : `calc(${adaptWidth(left)} - 380px)`,
      ...props.style,
    }}
  >
    {children}
  </CardContainer>
)

export const AgentTour = ({ onClose }: { onClose: () => void }) => {
  const [state, dispatch] = React.useReducer(tourReducer, initialState)
  const next = React.useCallback(() => dispatch({ type: "next" }), [])
  const prev = React.useCallback(() => dispatch({ type: "prev" }), [])

  React.useEffect(() => {
    const listener = (ev: KeyboardEvent) => {
      if (ev.keyCode === 37) {
        // left
        prev()
      } else if (ev.keyCode === 39) {
        // right
        next()
      }
    }
    document.addEventListener("keydown", listener)
    return () => document.removeEventListener("keydown", listener)
  }, [next, prev])

  const currentSlide = slides[state.currentSlide]
  const currentCard = currentSlide?.cards[state.currentCard]

  // Preload next image
  const nextSlideImgUrl =
    state.currentSlide + 1 < slides.length
      ? slides[state.currentSlide + 1].imgUrl
      : null
  React.useEffect(() => {
    new Image().src = nextSlideImgUrl
  }, [nextSlideImgUrl])

  // Timeout handling
  React.useEffect(() => {
    const cardTimeout =
      currentCard?.behaviour === "timeout" ? currentCard.timeoutMs : null
    if (!cardTimeout) return
    const t = setTimeout(next, cardTimeout)
    return () => clearTimeout(t)
  }, [currentCard, next])

  if (!currentCard) return null

  return (
    <>
      <MainImg src={currentSlide.imgUrl} />

      {currentCard.behaviour === "welcome" ? (
        <>
          <FullscreenBackdrop />
          <CenterCardContainer
            style={{ backgroundColor: colors.white, color: colors.darkgrey }}
          >
            <CardTitle>{currentCard.title}</CardTitle>
            <CardDescription>{currentCard.description}</CardDescription>
            <CardFooter>
              <CardButton onClick={next}>Next</CardButton>
            </CardFooter>
          </CenterCardContainer>
        </>
      ) : currentCard.behaviour === "hotspot" ? (
        <>
          <Hotspot {...currentCard.hotspot} onClick={next} />
          <CardContainerAtPos
            {...currentCard.hotspot}
            style={{
              backgroundColor: colors.darkgrey,
              color: colors.white,
            }}
            noSwap={currentCard.noSwap}
          >
            {currentCard.title && <CardTitle>{currentCard.title}</CardTitle>}
            <CardDescription>{currentCard.description}</CardDescription>
            <CardFooter>
              <CardButton onClick={next}>Next</CardButton>
              <div style={{ flex: 1 }} />
              <CardButton onClick={prev}>Previous</CardButton>
            </CardFooter>
          </CardContainerAtPos>
        </>
      ) : currentCard.behaviour === "tour" ? (
        <>
          <HighlightElement
            style={{
              opacity: currentCard.highlight ? 1 : 0,
              top: adaptHeight(currentCard.highlight?.top),
              left: adaptWidth(currentCard.highlight?.left),
              bottom: adaptHeight(currentCard.highlight?.bottom) ?? "unset",
            }}
          />
          <CardContainerAtPos
            {...currentCard.hotspot}
            style={{
              backgroundColor: currentCard.highlight
                ? colors.white
                : colors.darkgrey,
              color: currentCard.highlight ? colors.darkgrey : colors.white,
            }}
            noSwap={Boolean(currentCard.noSwap)}
          >
            {currentCard.title && <CardTitle>{currentCard.title}</CardTitle>}
            <CardDescription>{currentCard.description}</CardDescription>
            <CardFooter>
              <CardButton onClick={next}>Next</CardButton>
              <div style={{ flex: 1 }} />
              <CardButton onClick={prev}>Previous</CardButton>
            </CardFooter>
          </CardContainerAtPos>
        </>
      ) : currentCard.behaviour === "cta" ? (
        <>
          <FullscreenBackdrop />
          <CenterCardContainer
            style={{
              alignItems: "stretch",
              maxWidth: 320,
              background: colors.white,
            }}
          >
            <CardTitle>{currentCard.title}</CardTitle>
            <CardDescription>{currentCard.description}</CardDescription>
            <CardButtonCTA
              as={Link}
              // @ts-expect-error untyped `to`
              to={`/request-demo`}
            >
              Book a custom demo
            </CardButtonCTA>
            <CardButton style={{ alignSelf: "center" }} onClick={onClose}>
              End the tour
            </CardButton>
          </CenterCardContainer>
        </>
      ) : null}
    </>
  )
}
